/**
 * WorkspaceSettingsContext — workspace_settings 캐싱 Provider
 *
 * activeWorkspaceId 가 바뀔 때마다 1회 로드. 변경 시 refresh.
 * useWorkspaceSettings() / useCanManageBoards() 훅으로 노출.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAppUIContext } from './AppUIContext';
import { useMyRole } from './WorkspacePermissionContext';
import { WorkspaceSettings, defaultWorkspaceSettings } from '../models/WorkspaceSettings';
import { fetchWorkspaceSettings } from '../services/WorkspaceSettingsService';
import { My } from '../services/My';

interface SettingsState {
  settings: WorkspaceSettings | null;
  refresh: () => Promise<void>;
}

const WorkspaceSettingsContext = createContext<SettingsState>({
  settings: null,
  refresh: async () => {},
});

/** 현재 워크스페이스 설정. 미로드/미설정 시 defaults 반환 (절대 null 반환 안 함 — caller 분기 줄임). */
export function useWorkspaceSettings(): WorkspaceSettings {
  const { activeWorkspaceId } = useAppUIContext();
  const { settings } = useContext(WorkspaceSettingsContext);
  return settings ?? defaultWorkspaceSettings(activeWorkspaceId ?? '');
}

/** 강제 refresh 함수 — 설정 변경 후 호출 */
export function useRefreshWorkspaceSettings(): () => Promise<void> {
  return useContext(WorkspaceSettingsContext).refresh;
}

/**
 * 보드 관리 권한 — owner/admin 의 canManageBoards 또는
 * member 라도 board_manager_user_ids 에 포함되면 true.
 */
export function useCanManageBoards(): boolean {
  const role = useMyRole();
  const settings = useWorkspaceSettings();
  const myId = My.user?.id;
  if (!role || !myId) return false;
  if (role === 'owner' || role === 'admin') return true;
  if (role === 'member' && settings.board_manager_user_ids.includes(myId)) return true;
  return false;
}

export const WorkspaceSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activeWorkspaceId } = useAppUIContext();
  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) {
      setSettings(null);
      return;
    }
    try {
      const s = await fetchWorkspaceSettings(activeWorkspaceId);
      setSettings(s);
    } catch (err) {
      console.error('[WorkspaceSettings] 로드 실패:', err);
      setSettings(defaultWorkspaceSettings(activeWorkspaceId));
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    // 워크스페이스 전환 즉시 리셋 → defaults 가 잠시 노출 후 fresh fetch 로 교체
    setSettings(null);
    load();
  }, [activeWorkspaceId, load]);

  return (
    <WorkspaceSettingsContext.Provider value={{ settings, refresh: load }}>
      {children}
    </WorkspaceSettingsContext.Provider>
  );
};

export default WorkspaceSettingsContext;
